/**
 * Survey Response Page for Re:Nav-96
 *
 * Allows authenticated members to view and respond to surveys.
 * Fetches survey by ID and renders the survey form for submission.
 *
 * Features:
 * - Survey information display (title, description)
 * - Dynamic form rendering
 * - Response submission
 * - Success confirmation with optional results viewing
 * - Error handling
 *
 * @example
 * // In router configuration
 * { path: 'surveys/:id', element: <SurveyPage /> }
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { surveysApi, ApiClientError } from '@/services/api';
import { SurveyForm } from '@/components/member/SurveyForm';
import type { SurveyForm as SurveyFormType } from '@/types';

/**
 * Loading spinner component
 */
function LoadingSpinner() {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <svg className="animate-spin h-12 w-12 text-primary mb-4" viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <p className="text-white/60">Loading survey...</p>
    </div>
  );
}

/**
 * Error state component
 */
function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="text-center py-16">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 mb-4">
        <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-white mb-2">Unable to load survey</h2>
      <p className="text-white/60 mb-6">{message}</p>
      <div className="flex justify-center gap-4">
        {onRetry && (
          <button type="button" onClick={onRetry} className="btn-primary">
            Try Again
          </button>
        )}
        <Link to="/member/dashboard" className="btn-secondary">
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}

/**
 * Survey not found component
 */
function NotFoundState() {
  return (
    <div className="text-center py-16">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-500/10 mb-4">
        <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-white mb-2">Survey Not Found</h2>
      <p className="text-white/60 mb-6">
        The survey you're looking for doesn't exist or has been removed.
      </p>
      <Link to="/member/dashboard" className="btn-primary">
        Back to Dashboard
      </Link>
    </div>
  );
}

/**
 * Survey inactive state component
 */
function InactiveState({ survey }: { survey: SurveyFormType }) {
  return (
    <div className="text-center py-16">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-500/10 mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-white mb-2">Survey Closed</h2>
      <p className="text-white/60 mb-2">{survey.title}</p>
      <p className="text-white/40 mb-6">This survey is no longer accepting responses.</p>
      <Link to="/member/dashboard" className="btn-primary">
        Back to Dashboard
      </Link>
    </div>
  );
}

/**
 * Success state component shown after submission
 */
function SuccessState({
  survey,
  showViewResults,
}: {
  survey: SurveyFormType;
  showViewResults: boolean;
}) {
  return (
    <div className="text-center py-16">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/10 mb-6 animate-fade-in">
        <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <h2 className="text-2xl font-display font-bold text-white mb-2">Thank You!</h2>
      <p className="text-white/60 mb-2">
        Your response to "{survey.title}" has been submitted successfully.
      </p>
      <p className="text-white/40 text-sm mb-8">
        We appreciate you taking the time to share your thoughts.
      </p>
      <div className="flex justify-center gap-4 flex-wrap">
        <Link to="/member/dashboard" className="btn-primary">
          Back to Dashboard
        </Link>
        {showViewResults && survey.resultsPublic && (
          <Link to={`/member/surveys/${survey.id}/results`} className="btn-secondary">
            View Results
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * Survey header component
 */
function SurveyHeader({ survey }: { survey: SurveyFormType }) {
  const questionCount = survey.formSchema.questions.length;

  return (
    <div className="mb-8">
      <Link
        to="/member/dashboard"
        className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-4"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Dashboard
      </Link>

      <div className="card">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
            <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-display font-bold text-white mb-2">
              {survey.title}
            </h1>
            {survey.description && (
              <p className="text-white/60 leading-relaxed">{survey.description}</p>
            )}
            <div className="flex items-center gap-4 mt-4 text-sm text-white/40">
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {questionCount} question{questionCount !== 1 ? 's' : ''}
              </span>
              {survey.formSchema.questions.some((q) => q.required) && (
                <span className="flex items-center gap-1">
                  <span className="text-primary">*</span>
                  Required fields
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * SurveyPage Component
 *
 * Main page for viewing and responding to surveys.
 * Handles loading, error states, and form submission.
 *
 * @returns The survey page component
 */
export function SurveyPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const surveyId = params.id;

  // State
  const [survey, setSurvey] = useState<SurveyFormType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [notFound, setNotFound] = useState(false);

  /**
   * Fetch survey data
   */
  const fetchSurvey = useCallback(async () => {
    if (!surveyId) {
      setNotFound(true);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setNotFound(false);

      const data = await surveysApi.get(surveyId);
      setSurvey(data);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 404) {
        setNotFound(true);
      } else {
        const message = err instanceof ApiClientError ? err.message : 'Failed to load survey';
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [surveyId]);

  /**
   * Load survey on mount
   */
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchSurvey();
    }
  }, [authLoading, isAuthenticated, fetchSurvey]);

  /**
   * Redirect if not authenticated
   */
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', {
        state: { from: `/member/surveys/${surveyId}` },
        replace: true,
      });
    }
  }, [authLoading, isAuthenticated, navigate, surveyId]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (responses: Record<string, unknown>) => {
      if (!surveyId) return;

      try {
        setIsSubmitting(true);
        setError(null);

        await surveysApi.submit(surveyId, responses);
        setIsSubmitted(true);
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to submit response';
        setError(message);

        // Scroll to top to show error
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [surveyId]
  );

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <LoadingSpinner />
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Loading state */}
      {isLoading && <LoadingSpinner />}

      {/* Not found state */}
      {!isLoading && notFound && <NotFoundState />}

      {/* Error state */}
      {!isLoading && !notFound && error && !survey && (
        <ErrorState message={error} onRetry={fetchSurvey} />
      )}

      {/* Survey content */}
      {!isLoading && !notFound && survey && (
        <>
          {/* Inactive survey */}
          {!survey.isActive && !isSubmitted && <InactiveState survey={survey} />}

          {/* Success state */}
          {isSubmitted && <SuccessState survey={survey} showViewResults={survey.resultsPublic} />}

          {/* Active survey form */}
          {survey.isActive && !isSubmitted && (
            <>
              <SurveyHeader survey={survey} />

              {/* Error banner */}
              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-red-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-red-400">{error}</p>
                  <button
                    type="button"
                    onClick={() => setError(null)}
                    className="ml-auto text-red-400/60 hover:text-red-400"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )}

              {/* Survey form */}
              <SurveyForm
                survey={survey}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

export default SurveyPage;
